'use client';

import { useMemo } from 'react';

import { Popover, Button, Stack, Skeleton } from '@backyard-ui/core';

import { type ItemId } from '@/infra/services/catalog/model';
import { ANCHORS } from '@/presenters/components/catalog/constants';
import { useSummary } from '@/presenters/hooks/catalog/use-summary';
import { useDefaultStore } from '@/presenters/store/catalog/default';

import { RatingStars } from '../../common/RatingStars';
import { RatingResume } from '../RatingResume';

interface ReviewPopoverProps {
  itemId: ItemId;
}

export default function ReviewPopover(props: Readonly<ReviewPopoverProps>) {
  const { itemId } = props;

  const initialSummary = useDefaultStore((store) => store.review.summary);

  const { data: summary, isFetching } = useSummary(itemId, {
    initialData: initialSummary && { data: initialSummary },
  });

  const distribution = summary?.data.generalScores?.distribution;
  const statistics = summary?.data.generalScores?.statistics;
  const avgRating = Number(statistics?.avgRating ?? 0);
  const totalReviewsCount = statistics?.totalReviewsCount ?? 0;

  const content = useMemo(() => {
    return (
      <RatingStars
        rating={avgRating}
        totalReviewsCount={totalReviewsCount}
        showRating
      />
    );
  }, [avgRating, totalReviewsCount]);

  if (isFetching) {
    return <Skeleton height="16px" width="128px" data-testid="skeleton" />;
  }

  if (!distribution) {
    return content;
  }

  return (
    <Popover.Root data-testid="review-popover">
      <Popover.Trigger>{content}</Popover.Trigger>
      <Popover.Content align="start">
        <Popover.Body>
          <Stack direction="column" className="p-2">
            <RatingResume
              distribution={distribution}
              totalReviewsCount={totalReviewsCount}
              size="sm"
            />
            <Button variant="ghost" size="xs" asChild>
              <a href={`#${ANCHORS.reviews}`}>
                Ler {totalReviewsCount}{' '}
                {totalReviewsCount > 1 ? 'Avaliações' : 'Avaliação'}
              </a>
            </Button>
          </Stack>
        </Popover.Body>
      </Popover.Content>
    </Popover.Root>
  );
}
